import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaPlus, FaImages, FaEye, FaEdit, FaQrcode, FaUsers, FaSearch, FaTimes, FaArchive, FaRedoAlt, FaArrowRight } from 'react-icons/fa';
import { getEvents, getImageUrl, getMySubscription, reactivateGallery } from '../services/api';
import { isEventManager } from '../utils/roles';
import './PhotographerDashboard.css';

const visibilityLabels = {
  public: 'PUBLIC',
  unlisted: 'NON LISTÉ',
  private: 'PRIVÉ'
};

const normalizeSearch = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr-FR');

const PhotographerDashboard = () => {

  console.log('PhotographerDashboard rendered'); // Debugging log
  
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const [subscription, setSubscription] = useState(null);
  const [reactivating, setReactivating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || '');

  const searchWords = normalizeSearch(searchTerm).trim().split(/\s+/).filter(Boolean);
  const filteredEvents = events.filter((event) => {
    const searchableText = normalizeSearch([event.name, event.location, event.category].filter(Boolean).join(' '));
    return searchWords.every((word) => searchableText.includes(word));
  });

  const clearSearch = () => {
    setSearchTerm('');
    searchInputRef.current?.focus();
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const fetchEvents = async () => {
    try {
      const [response, current] = await Promise.all([getEvents(), getMySubscription()]);
      setEvents(response.events);
      setSubscription(current);
    } catch (err) {
      setError(err.message || 'Impossible de charger les événements');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (eventId) => {
    setReactivating(eventId);
    setError('');
    try {
      await reactivateGallery(eventId);
      await fetchEvents();
      setSuccessMessage('La galerie est de nouveau active.');
    } catch (err) {
      setError(err.errors?.subscription?.[0] || err.response?.data?.errors?.subscription?.[0] || err.response?.data?.message || err.message || 'Impossible de réactiver la galerie.');
    } finally {
      setReactivating(null);
    }
  };

  const eventLimit = subscription?.plan_details?.events_monthly_limit;
  const canCreate = eventLimit != null && (eventLimit === -1 || subscription?.usage?.events_created_this_month < eventLimit);

  const getVisibilityBadge = (visibility) => {
    const colors = {
      public: '#4ADE80',
      unlisted: '#4F46E5',
      private: '#C084FC'
    };
    
    return (
      <span style={{
        background: colors[visibility] || colors.public,
        color: 'white',
        padding: '0.3rem 0.8rem',
        borderRadius: '15px',
        fontSize: '0.8rem',
        fontWeight: '600',
        marginLeft: '0.5rem'
      }}>
        {visibilityLabels[visibility] || 'PUBLIC'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner"></div>
        <p>Chargement de vos événements...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1>Mes événements photo</h1>
        <p>Gérez vos événements et vos imports de photos</p>
      </div>

      {eventLimit != null && !canCreate && <div role="alert" className="error-message">
        Limite mensuelle d’événements atteinte. <Link to="/pricing">Choisir un forfait supérieur</Link> ou attendre le prochain mois.
      </div>}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {canCreate ? <Link to="/create-event" className="btn-primary">
            <FaPlus /> Créer un nouvel événement
          </Link> : <button className="btn-primary" disabled>Création indisponible</button>}
          {isEventManager(user) && (
            <Link
              to="/manager/photographers"
              className="btn-primary"
              style={{ background: '#A855F7' }}
            >
              <FaUsers /> Mon équipe
            </Link>
          )}
        </div>
        {user?.subscription_plan && (
          <div style={{
            padding: '0.8rem 1.5rem',
            background: 'var(--bg)',
            borderRadius: '8px',
            fontSize: '0.9rem'
          }}>
            <strong>Forfait :</strong> {subscription?.plan_details?.name || 'Gratuit'} •
            <strong> Événements ce mois :</strong> {subscription?.usage?.events_created_this_month ?? '—'} / {subscription?.plan_details?.events_monthly_limit ?? '—'}
            {' • '}<strong>Photos ce mois :</strong> {subscription?.usage?.photos_uploaded_this_month ?? '—'} / {subscription?.plan_details?.photos_monthly_limit ?? '—'}
            {' • '}<strong>Stockage actif :</strong> {subscription?.usage?.storage_used_mb ?? '—'} / {subscription?.plan_details?.storage_mb ?? '—'} Mo
          </div>
        )}
      </div>

      {events.length > 0 && (
        <div className="photographer-events-search" role="search" aria-label="Rechercher dans mes événements">
          <div className="photographer-events-search-field">
            <label htmlFor="photographer-event-search">Rechercher un événement</label>
            <div className="photographer-events-search-control">
              <FaSearch aria-hidden="true" />
              <input
                ref={searchInputRef}
                id="photographer-event-search"
                type="search"
                placeholder="Nom, lieu ou catégorie…"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                aria-controls="photographer-events-results"
              />
              {searchTerm && (
                <button type="button" onClick={clearSearch} aria-label="Effacer la recherche">
                  <FaTimes aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
          <p className="photographer-events-search-count" role="status">
            {filteredEvents.length} événement{filteredEvents.length === 1 ? '' : 's'}
            {searchWords.length > 0 && ` sur ${events.length}`}
          </p>
        </div>
      )}

      {events.length === 0 ? (
        <div style={{ 
          padding: '3rem', 
          textAlign: 'center', 
          background: 'var(--surface)', 
          borderRadius: '15px',
          boxShadow: '0 4px 8px var(--shadow)'
        }}>
          <FaCalendarAlt style={{ fontSize: '4rem', color: 'var(--gray)', marginBottom: '1rem' }} />
          <h3 style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>
            Aucun événement pour le moment
          </h3>
          <p style={{ color: 'var(--gray)', marginBottom: '2rem' }}>
            Créez votre premier événement pour commencer à importer et organiser vos photos
          </p>
          {canCreate && <Link to="/create-event" className="btn-primary">
            Créer votre premier événement
          </Link>}
        </div>
      ) : filteredEvents.length === 0 ? (
        <div id="photographer-events-results" className="photographer-events-no-results">
          <FaSearch aria-hidden="true" />
          <h3>Aucun événement trouvé</h3>
          <p>Essayez un autre nom, lieu ou catégorie.</p>
          <button type="button" className="btn-primary" onClick={clearSearch}>
            Afficher tous les événements
          </button>
        </div>
      ) : (
        <div id="photographer-events-results" className="events-list">
          {filteredEvents.map((event) => {
            const photoCount = Number(event.photos_count ?? event.photo_count ?? 0);

            return (
            <div key={event.id} className={`event-card${event.is_archived ? ' event-card--archived' : ''}`}>
              <div className="event-card-cover" style={{
                height: '150px',
                margin: '-1.5rem -1.5rem 1rem',
                background: event.is_archived ? 'var(--surface-2)' : 'linear-gradient(135deg, #E8EDFF 0%, var(--primary) 100%)',
                overflow: 'hidden',
              }}>
                {event.is_archived ? (
                  <div className="archived-gallery-cover">
                    <span className="archived-gallery-symbol"><FaArchive aria-hidden="true" /></span>
                    <span className="archived-gallery-badge">Galerie archivée</span>
                  </div>
                ) : event.cover_photo ? (
                  <img
                    src={getImageUrl(event.cover_photo)}
                alt={`Couverture de ${event.name}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgba(255,255,255,0.85)',
                    fontSize: '2.5rem',
                  }}>
                    <FaImages />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <h3>{event.name}</h3>
                {getVisibilityBadge(event.visibility)}
              </div>
              {!event.is_archived && event.retention_expires_at && (
                <p>Conservation jusqu’au {new Date(event.retention_expires_at).toLocaleDateString('fr-FR')}</p>
              )}
              <div style={{ marginTop: '0.5rem' }}>
                <span className="category-badge">{event.category}</span>
              </div>
              <p style={{ color: 'var(--gray)', marginTop: '1rem' }}>
                {event.description || 'Aucune description'}
              </p>
              <div style={{ 
                marginTop: '1rem', 
                paddingTop: '1rem', 
                borderTop: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.75rem',
                fontSize: '0.9rem',
                color: 'var(--gray)'
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.35rem 0.65rem',
                  borderRadius: '8px',
                  background: 'var(--primary-tint)',
                  color: 'var(--primary-strong)',
                  whiteSpace: 'nowrap'
                }}>
                  <FaImages aria-hidden="true" />
                  <span><strong>{photoCount.toLocaleString('fr-FR')}</strong> photo{photoCount === 1 ? '' : 's'}</span>
                </span>
                {event.event_date && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
                    <FaCalendarAlt aria-hidden="true" />
                    {new Date(event.event_date).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>
              
              {event.is_archived && (
                <section className="archived-gallery-panel" aria-label={`Réactivation de ${event.name}`}>
                  <h4>Vos photos sont conservées</h4>
                  <p>Cette galerie n’est plus accessible aux visiteurs. Réactivez-la si votre forfait le permet, ou choisissez un forfait supérieur.</p>
                  <div className="archived-gallery-actions">
                    <button
                      type="button"
                      className="archived-gallery-reactivate"
                      disabled={reactivating === event.id}
                      aria-busy={reactivating === event.id}
                      onClick={() => handleReactivate(event.id)}
                    >
                      <FaRedoAlt aria-hidden="true" />
                      {reactivating === event.id ? 'Réactivation…' : 'Réactiver la galerie'}
                    </button>
                    <Link to="/pricing" className="archived-gallery-upgrade">
                      Comparer les forfaits <FaArrowRight aria-hidden="true" />
                    </Link>
                  </div>
                </section>
              )}
              <div className="event-card-tools">
                {!event.is_archived && <Link to={`/event/${event.id}/photos`} className="event-tool-import"><FaImages aria-hidden="true" /> Importer</Link>}
                <Link to={`/event/${event.id}/browse`} className="event-tool-browse"><FaEye aria-hidden="true" /> Parcourir</Link>
                <Link to={`/event/${event.id}/edit`} className="event-tool-edit"><FaEdit aria-hidden="true" /> Modifier</Link>
                <Link to={`/event/${event.id}/qr`} className="event-tool-qr"><FaQrcode aria-hidden="true" /> QR</Link>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PhotographerDashboard;
